import React from 'react';
import { ReactComponent as FacebookIcon } from '../images/facebook.svg'
import { ReactComponent as TwitterIcon } from '../images/twitter.svg'
import { ReactComponent as XIcon } from '../images/x.svg'
import { ReactComponent as InstagramIcon } from '../images/instagram.svg'
import { ReactComponent as Quote } from '../images/quote.svg'
import { ReactComponent as QuoteCGI } from '../images/quote-cgi.svg'
import { ReactComponent as QuoteLGI } from '../images/quote-lgi.svg'
import NewsroomList from './NewsroomList';
import { formatCountyName } from '../utils/dataTransform';

const MayorView: React.FunctionComponent<any> = ({ details, news }) => {
  const getColor = (person: any) => {
    if (person.isLGI) {
      if (person.pilot_program === "Yes") {
        return "bg-main-orange";
      }
      return "bg-light-orange";
    } else if (person.isCGI) {
      if (person.pilot_program === "Yes") {
        return "bg-main-green";
      }
      return "bg-light-green";
    } else {
      if (person.pilot_program === "Yes" || person.yes === "Yes") {
        return "bg-main-purple";
      }
      return "bg-light-purple";
    }
  }

  // Check if photo exists
  const photoUrl = details.mayor_photo_link || details.county_officials_photo_link || details.legislator_photo_link || details.legislators_photo_link;
  const hasPhoto = photoUrl && photoUrl !== '' && photoUrl !== 'NA';
  
  // Check if quote exists
  const quote = details.mayor_quote || details.county_officials_quote || details.legislators_quote;
  const hasQuote = quote && quote !== '' && quote !== 'NA';

  // Check if social links exist
  const hasSocialLinks = (details.facebook && details.facebook !== 'NA' && details.facebook !== '') ||
                         (details.twitter && details.twitter !== 'NA' && details.twitter !== '') ||
                         (details.x && details.x !== 'NA' && details.x !== '') ||
                         (details.instagram && details.instagram !== 'NA' && details.instagram !== '');

  return (
    <div className="overflow-y-auto px-2.5 md:px-6" style={{ height: 'calc(100% - 120px)' }}>
      <div className="flex items-center pb-3">
        <div className="flex-none block w-20 h-20 border-4 p-1 border-black">
          <span className={`block w-full h-full ${getColor(details)}`}></span>
        </div>
        <div className="flex-1 pl-4">
          <p className="block text-xl font-bold">{details.isLGI ? details.district : (details.city || formatCountyName(details.county, details.state))}, {details.state}</p>
          {(details.pilot_program === 'Yes' || details.yes === 'Yes') &&
            <p className="block text-sm">{details.city || formatCountyName(details.county, details.state)}, {details.state} has a pilot program.</p>
          }
        </div>
      </div>
      <div className="py-6 border-t border-black">
        <span className="block text-xl font-bold mb-2">{details.mayors_name || details.county_officials_name || details.legislators_name}</span>
        
        {/* Case 1: Photo exists but no quote - Enlarged photo with centered social links */}
        {hasPhoto && !hasQuote && (
          <div className="flex flex-col items-center">
            <div className="w-48 h-48 border-4 border-black relative">
              <div className="border-4 border-white" style={{
                width: '184px', height: '184px', backgroundSize: 'cover', backgroundPosition: 'center', backgroundImage: `url(${photoUrl})`
              }}></div>
            </div>
            {hasSocialLinks && (
              <div className="flex items-center justify-center my-4 gap-3">
                {(details.facebook && details.facebook !== 'NA' && details.facebook !== '') &&
                  <a href={`https://www.facebook.com/${details.facebook}`} target="_blank" rel="noreferrer">
                    <FacebookIcon />
                  </a>
                }
                {(details.twitter && details.twitter !== 'NA' && details.twitter !== '') && 
                  <a href={`https://twitter.com/${details.twitter}`} target="_blank" rel="noreferrer">
                    <TwitterIcon />
                  </a>
                }
                {(details.x && details.x !== 'NA' && details.x !== '') && 
                  <a href={`https://x.com/${details.x}`} target="_blank" rel="noreferrer">
                    <XIcon />
                  </a>
                }
                {(details.instagram && details.instagram !== 'NA' && details.instagram !== '' ) &&
                  <a href={`https://instagram.com/${details.instagram}`} target="_blank" rel="noreferrer">
                    <InstagramIcon />
                  </a>
                }
              </div>
            )}
          </div>
        )}

        {/* Case 2: Quote exists but no photo - Social links above full-width quote */}
        {!hasPhoto && hasQuote && (
          <div>
            {hasSocialLinks && (
              <div className="flex items-center gap-3 mb-4">
                {(details.facebook && details.facebook !== 'NA' && details.facebook !== '') &&
                  <a href={`https://www.facebook.com/${details.facebook}`} target="_blank" rel="noreferrer">
                    <FacebookIcon />
                  </a>
                }
                {(details.twitter && details.twitter !== 'NA' && details.twitter !== '') && 
                  <a href={`https://twitter.com/${details.twitter}`} target="_blank" rel="noreferrer">
                    <TwitterIcon />
                  </a>
                }
                {(details.x && details.x !== 'NA' && details.x !== '') && 
                  <a href={`https://x.com/${details.x}`} target="_blank" rel="noreferrer">
                    <XIcon />
                  </a>
                }
                {(details.instagram && details.instagram !== 'NA' && details.instagram !== '' ) &&
                  <a href={`https://instagram.com/${details.instagram}`} target="_blank" rel="noreferrer">
                    <InstagramIcon />
                  </a>
                }
              </div>
            )}
            <div className="text-sm font-light py-4 relative">
              <p className="relative z-10">{quote}</p>
              { details.isLGI &&
                <QuoteLGI className="absolute top-0 left-0" />
              }
              { details.isCGI &&
                <QuoteCGI className="absolute top-0 left-0" />
              }
              { !details.isCGI && !details.isLGI &&
                <Quote className="absolute top-0 left-0" />
              }
            </div>
          </div>
        )}

        {/* Case 3: Both photo and quote exist - Original layout */}
        {hasPhoto && hasQuote && (
          <div className="flex">
            <div className="flex-none">
              <div className="w-28 h-28 border-4 border-black relative">
                <div className="border-4 border-white" style={{
                  width: '104px', height: '104px', backgroundSize: 'cover', backgroundPosition: 'center', backgroundImage: `url(${photoUrl})`
                }}></div>
              </div>
              <div className="flex items-center justify-center my-2 gap-2">
                {(details.facebook && details.facebook !== 'NA' && details.facebook !== '') &&
                  <a href={`https://www.facebook.com/${details.facebook}`} target="_blank" rel="noreferrer">
                    <FacebookIcon />
                  </a>
                }
                {(details.twitter && details.twitter !== 'NA' && details.twitter !== '') && 
                  <a href={`https://twitter.com/${details.twitter}`} target="_blank" rel="noreferrer">
                    <TwitterIcon />
                  </a>
                }
                {(details.x && details.x !== 'NA' && details.x !== '') && 
                  <a href={`https://x.com/${details.x}`} target="_blank" rel="noreferrer">
                    <XIcon />
                  </a>
                }
                {(details.instagram && details.instagram !== 'NA' && details.instagram !== '' ) &&
                  <a href={`https://instagram.com/${details.instagram}`} target="_blank" rel="noreferrer">
                    <InstagramIcon />
                  </a>
                }
              </div>
            </div>
            <div className="flex-1 text-sm font-light pl-3 py-4 relative">
              <p className="relative z-10">{quote}</p>
              { details.isLGI &&
                <QuoteLGI className="absolute top-0 left-2" />
              }
              { details.isCGI &&
                <QuoteCGI className="absolute top-0 left-2" />
              }
              { !details.isCGI && !details.isLGI &&
                <Quote className="absolute top-0 left-2" />
              }
            </div>
          </div>
        )}

        {/* Case 4: Neither photo nor quote exist - Just show social links if available */}
        {!hasPhoto && !hasQuote && hasSocialLinks && (
          <div className="flex items-center gap-3 mt-4">
            {(details.facebook && details.facebook !== 'NA' && details.facebook !== '') &&
              <a href={`https://www.facebook.com/${details.facebook}`} target="_blank" rel="noreferrer">
                <FacebookIcon />
              </a>
            }
            {(details.twitter && details.twitter !== 'NA' && details.twitter !== '') && 
              <a href={`https://twitter.com/${details.twitter}`} target="_blank" rel="noreferrer">
                <TwitterIcon />
              </a>
            }
            {(details.x && details.x !== 'NA' && details.x !== '') && 
              <a href={`https://x.com/${details.x}`} target="_blank" rel="noreferrer">
                <XIcon />
              </a>
            }
            {(details.instagram && details.instagram !== 'NA' && details.instagram !== '' ) &&
              <a href={`https://instagram.com/${details.instagram}`} target="_blank" rel="noreferrer">
                <InstagramIcon />
              </a>
            }
          </div>
        )}
      </div>
      {((details.pilot_program === 'Yes' || details.yes === 'Yes') && (details.pilot_description || details.pilot_link)) &&
        <div className="py-6 border-t border-black">
          <p className="block text-xl font-bold">About the Pilot</p>
          {details.pilot_description &&
            <p className="my-2 text-sm font-light">{details.pilot_description}</p>
          }
          {details.pilot_link &&
            <a className="border-2 border-black inline-block mt-2 p-2 text-xs font-bold uppercase" href={details.pilot_link} target="_blank" rel="noreferrer">Read More →</a>
          }
        </div>
      }
      {details.report_link &&
        <div className="py-6 border-t border-black">
          <div className={`p-6 ${details.isLGI ? 'bg-light-orange' : details.isCGI ? 'bg-light-green' : 'bg-light-purple'}`}>
            <p className="text-2xl font-bold mb-4">
              See the results of the pilot in {details.city || formatCountyName(details.county, details.state) || details.district}, {details.state}
            </p>
            <a 
              className="border-2 border-black inline-block p-2 text-xs font-bold uppercase" 
              href={details.report_link} 
              target="_blank" 
              rel="noreferrer"
            >
              Read More →
            </a>
          </div>
        </div>
      }
      {news?.length > 0 &&
        <NewsroomList data={news} />
      }
    </div>
  )
}

export default MayorView;